// v3.4.510 — صفحة «التحقق بخطوتين»: كل مستخدم يربط تطبيق المصادقة (Google Authenticator) ويدير رموزه الاحتياطية،
// والمدير يحدد السياسة (معطّل / اختياري / إلزامي)، جوالات الموظفين لرمز واتساب، إعادة الضبط، وقالب واتساب على الرقم الموحد.
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { twoFactor } from "../services/api";
import { CodeInput, BackupCodes, EnrollGuide } from "./TwoFactorStep";

const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", marginBottom: 16 };
const h2 = { fontSize: 16, fontWeight: 800, color: "#1e293b", margin: "0 0 10px" };
const btn = (bg, color = "#fff", border = "none") => ({ padding: "8px 16px", border, borderRadius: 8, background: bg, color, fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer" });
const chip = (bg, color) => ({ display: "inline-block", background: bg, color, borderRadius: 999, padding: "2px 10px", fontSize: 11.5, fontWeight: 800 });
const th = { padding: "9px 8px", textAlign: "right", fontWeight: 700, color: "#475569", fontSize: 12, whiteSpace: "nowrap" };
const td = { padding: "8px 8px", fontSize: 12.5, color: "#1e293b", borderTop: "1px solid #f1f5f9" };
const MODES = [
  ["off", "معطّل", "الدخول بحساب Google فقط."],
  ["optional", "اختياري", "الخطوة الثانية تُطلب فقط ممن ربط تطبيق المصادقة."],
  ["required", "إلزامي للجميع", "كل موظف يُدخل رمز التطبيق عند كل دخول، ومن لم يربطه يربطه عند أول دخول."],
];
const TPL_STATUS = { APPROVED: ["معتمد ✓", "#dcfce7", "#166534"], PENDING: ["بانتظار اعتماد ميتا", "#fef3c7", "#92400e"], REJECTED: ["مرفوض", "#fee2e2", "#991b1b"] };
const errOf = (e, fallback) => e.response?.data?.error || fallback;

function MySection({ me, reload }) {
  const [step, setStep] = useState(null); // null | "code" (طلب الرمز الحالي) | "enroll" | "backup"
  const [action, setAction] = useState(null); // relink | disable | backup
  const [code, setCode] = useState("");
  const [enroll, setEnroll] = useState(null);
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setStep(null); setAction(null); setCode(""); setEnroll(null); };
  const run = async (fn) => { if (busy) return; setBusy(true); try { await fn(); } catch (e) { toast.error(errOf(e, "فشلت العملية")); setCode(""); } finally { setBusy(false); } };

  const startEnroll = (current) => run(async () => { const r = await twoFactor.myStart(current); setEnroll(r.data); setCode(""); setStep("enroll"); });
  const confirmEnroll = () => run(async () => { const r = await twoFactor.myConfirm(code); setCodes(r.data.backup_codes || []); reset(); setStep("backup"); toast.success("تم ربط تطبيق المصادقة"); reload(); });
  const submitCurrent = () => {
    if (action === "relink") return startEnroll(code);
    if (action === "backup") return run(async () => { const r = await twoFactor.myBackup(code); setCodes(r.data.backup_codes || []); reset(); setStep("backup"); toast.success("رموز احتياطية جديدة — القديمة أُلغيت"); reload(); });
    return run(async () => { await twoFactor.myDisable(code); reset(); toast.success("أُلغي ربط تطبيق المصادقة"); reload(); });
  };
  const ask = (a) => { setAction(a); setCode(""); setCodes(null); setStep("code"); };

  return (
    <div style={card}>
      <h2 style={h2}>👤 حسابي</h2>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 13, color: "#334155" }}>
        <span>تطبيق المصادقة (Google Authenticator):</span>
        {me.totp ? <span style={chip("#dcfce7", "#166534")}>مربوط ✓</span> : <span style={chip("#fee2e2", "#991b1b")}>غير مربوط</span>}
        {me.totp && <span style={chip(me.backup_left > 2 ? "#f1f5f9" : "#fef3c7", me.backup_left > 2 ? "#475569" : "#92400e")}>رموز احتياطية متبقية: {me.backup_left}</span>}
        {me.whatsapp && <span style={chip("#dcfce7", "#166534")}>رمز واتساب: <span dir="ltr">{me.whatsapp}</span></span>}
      </div>
      <div style={{ fontSize: 12, color: "#64748b", margin: "8px 0 12px", lineHeight: 1.8 }}>
        {me.mode === "required" ? "التحقق بخطوتين إلزامي: تُدخل رمز التطبيق عند كل تسجيل دخول." : me.mode === "optional" ? "بعد ربط التطبيق يُطلب رمزه عند كل تسجيل دخول." : "السياسة معطّلة حاليًا — يمكنك ربط التطبيق من الآن ليكون جاهزًا عند تفعيلها."}
      </div>

      {step === null && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {!me.totp && <button type="button" disabled={busy} onClick={() => { setCodes(null); startEnroll(); }} style={btn("#2563eb")}>🔗 ربط تطبيق المصادقة</button>}
          {me.totp && <button type="button" onClick={() => ask("relink")} style={btn("#fff", "#1e293b", "1px solid #cbd5e1")}>📱 إعادة الربط (جوال جديد)</button>}
          {me.totp && <button type="button" onClick={() => ask("backup")} style={btn("#fff", "#1e293b", "1px solid #cbd5e1")}>🔑 رموز احتياطية جديدة</button>}
          {me.totp && me.mode !== "required" && <button type="button" onClick={() => ask("disable")} style={btn("#fff", "#dc2626", "1px solid #fecaca")}>إلغاء الربط</button>}
        </div>
      )}
      {step === "code" && (
        <div style={{ maxWidth: 340 }}>
          <div style={{ fontSize: 13, color: "#475569", marginBottom: 8 }}>
            {action === "relink" ? "أدخل الرمز الحالي من التطبيق (أو رمزًا احتياطيًا) لإعادة الربط:" : action === "backup" ? "أدخل الرمز الحالي من التطبيق لتوليد رموز احتياطية جديدة:" : "أدخل الرمز الحالي من التطبيق لإلغاء الربط:"}
          </div>
          <CodeInput value={code} onChange={setCode} onEnter={submitCurrent} text={action === "relink"} />
          {action === "relink" && <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4 }}>6 أرقام من التطبيق، أو رمز احتياطي بصيغة xxxx-xxxx.</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="button" disabled={busy || code.length < 6} onClick={submitCurrent} style={btn(busy || code.length < 6 ? "#cbd5e1" : "#2563eb")}>متابعة</button>
            <button type="button" onClick={reset} style={btn("#fff", "#475569", "1px solid #cbd5e1")}>إلغاء</button>
          </div>
        </div>
      )}
      {step === "enroll" && enroll && (
        <div style={{ maxWidth: 380 }}>
          <EnrollGuide data={enroll} />
          <CodeInput value={code} onChange={setCode} onEnter={confirmEnroll} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="button" disabled={busy || code.length !== 6} onClick={confirmEnroll} style={btn(busy || code.length !== 6 ? "#cbd5e1" : "#16a34a")}>تأكيد وربط</button>
            <button type="button" onClick={reset} style={btn("#fff", "#475569", "1px solid #cbd5e1")}>إلغاء</button>
          </div>
        </div>
      )}
      {step === "backup" && codes && (
        <div style={{ maxWidth: 380 }}>
          <BackupCodes codes={codes} />
          <button type="button" onClick={() => { setCodes(null); setStep(null); }} style={btn("#16a34a")}>حفظت الرموز</button>
        </div>
      )}
    </div>
  );
}

function AdminSection({ me, reloadMe }) {
  const [data, setData] = useState(null);
  const [phones, setPhones] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => twoFactor.admin().then((r) => { setData(r.data); setPhones({}); }).catch((e) => toast.error(errOf(e, "تعذّر تحميل إعدادات المدير"))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn) => { if (busy) return; setBusy(true); try { await fn(); } catch (e) { toast.error(errOf(e, "فشلت العملية")); } finally { setBusy(false); } };

  const setMode = (mode) => {
    if (mode === data.mode) return;
    if (mode === "required" && !window.confirm("تفعيل «إلزامي للجميع»؟\n\n• كل موظف سيُطلب منه رمز تطبيق المصادقة عند كل دخول (ومن لم يربطه يربطه عند أول دخول).\n• الجلسات المفتوحة حاليًا للموظفين تُنهى ويعيدون الدخول.\n• احتفظ برموزك الاحتياطية.")) return;
    run(async () => { const r = await twoFactor.setPolicy(mode); toast.success("حُفظت السياسة" + (r.data.sessions_ended ? " — أُنهيت " + r.data.sessions_ended + " جلسة" : "")); await load(); reloadMe(); });
  };
  const savePhone = (u) => run(async () => { await twoFactor.setPhone(u.id, phones[u.id]); toast.success("حُفظ الجوال"); await load(); });
  const resetUser = (u) => {
    if (!window.confirm("إعادة ضبط التحقق بخطوتين للمستخدم " + (u.display_name || u.email) + "؟\nيُلغى ربط تطبيقه ورموزه الاحتياطية" + (data.mode === "required" ? " ويربط التطبيق من جديد عند دخوله القادم." : "."))) return;
    run(async () => { await twoFactor.resetUser(u.id); toast.success("أُعيد الضبط"); await load(); reloadMe(); });
  };
  const createTpl = () => { if (!window.confirm("إرسال قالب رمز الدخول إلى ميتا لاعتماده على الرقم الموحد؟")) return; run(async () => { const r = await twoFactor.createTemplate(); setData((d) => ({ ...d, wa: r.data.wa })); toast.success("أُرسل القالب — بانتظار اعتماد ميتا"); }); };
  const syncTpl = () => run(async () => { const r = await twoFactor.syncTemplate(); setData((d) => ({ ...d, wa: r.data.wa })); toast.success(r.data.found ? "حُدّثت الحالة" : "القالب غير موجود في ميتا بعد"); });

  if (!data) return <div style={{ ...card, color: "#94a3b8", fontSize: 13 }}>جارٍ التحميل…</div>;
  const wa = data.wa || {};
  const ts = TPL_STATUS[wa.template_status] || (wa.template_status ? [wa.template_status, "#f1f5f9", "#475569"] : ["لم يُرسل بعد", "#f1f5f9", "#475569"]);
  const linked = data.users.filter((u) => u.is_active && u.totp).length, active = data.users.filter((u) => u.is_active).length;

  return (
    <>
      <div style={card}>
        <h2 style={h2}>🛡️ سياسة الدخول للموظفين</h2>
        {data.bypass && <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, fontWeight: 700, marginBottom: 10 }}>مفتاح الطوارئ STAFF_2FA_BYPASS مفعّل في إعدادات الخادم — الخطوة الثانية معطّلة للجميع حتى يُزال.</div>}
        <div style={{ display: "grid", gap: 8 }}>
          {MODES.map(([k, label, desc]) => (
            <label key={k} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", border: "1px solid " + (data.mode === k ? "#2563eb" : "#e2e8f0"), background: data.mode === k ? "#eff6ff" : "#fff", borderRadius: 10, cursor: "pointer" }}>
              <input type="radio" name="mfa-mode" checked={data.mode === k} disabled={busy} onChange={() => setMode(k)} style={{ marginTop: 4 }} />
              <span><b style={{ fontSize: 13.5, color: "#1e293b" }}>{label}</b><br /><span style={{ fontSize: 12, color: "#64748b" }}>{desc}</span></span>
            </label>
          ))}
        </div>
        {!me.totp && <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", borderRadius: 8, padding: "8px 12px", marginTop: 10 }}>اربط تطبيق المصادقة لحسابك أولًا (من «حسابي» أعلاه) — لا يمكن تفعيل السياسة قبل ذلك حتى لا تُقفل على نفسك.</div>}
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 10 }}>ربطوا التطبيق: <b>{linked}</b> من <b>{active}</b> مستخدمًا نشطًا.</div>
      </div>

      <div style={card}>
        <h2 style={h2}>💬 رمز واتساب من الرقم الموحد (بديل لتطبيق المصادقة)</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 13, color: "#334155" }}>
          <span>الرقم: <b dir="ltr">{wa.number}</b></span>
          <span>قالب المصادقة <b dir="ltr">{wa.template}</b>:</span>
          <span style={chip(ts[1], ts[2])}>{ts[0]}</span>
          {!wa.template_status && <button type="button" disabled={busy} onClick={createTpl} style={btn("#16a34a")}>إرسال القالب لاعتماد ميتا</button>}
          <button type="button" disabled={busy} onClick={syncTpl} style={btn("#fff", "#1e293b", "1px solid #cbd5e1")}>🔄 تحديث الحالة</button>
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 8, lineHeight: 1.8 }}>
          {wa.ready ? "رمز واتساب مفعّل: يظهر كخيار عند الدخول لكل موظف سُجّل جواله في الجدول أدناه." : "حتى تعتمد ميتا القالب يبقى الدخول بتطبيق المصادقة فقط. بعد الاعتماد يظهر «رمز واتساب» تلقائيًا كخيار بديل لمن سُجّل جواله أدناه."}
        </div>
      </div>

      <div style={card}>
        <h2 style={h2}>👥 الموظفون</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#f8fafc" }}>
              <th style={th}>المستخدم</th><th style={th}>تطبيق المصادقة</th><th style={th}>رموز احتياطية</th><th style={th}>جوال واتساب</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {data.users.map((u) => {
                const val = phones[u.id] !== undefined ? phones[u.id] : (u.phone || "");
                const dirty = phones[u.id] !== undefined && phones[u.id] !== (u.phone || "");
                return (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                    <td style={td}><b>{u.display_name || "—"}</b>{u.role === "admin" && <span style={{ ...chip("#ede9fe", "#5b21b6"), marginInlineStart: 6 }}>مدير</span>}{!u.is_active && <span style={{ ...chip("#f1f5f9", "#64748b"), marginInlineStart: 6 }}>معطّل</span>}<br /><span dir="ltr" style={{ fontSize: 11.5, color: "#64748b" }}>{u.email}</span></td>
                    <td style={td}>{u.totp ? <span style={chip("#dcfce7", "#166534")}>مربوط ✓</span> : <span style={chip("#fee2e2", "#991b1b")}>غير مربوط</span>}{u.locked && <span style={{ ...chip("#fef3c7", "#92400e"), marginInlineStart: 6 }}>مقفل مؤقتًا</span>}</td>
                    <td style={{ ...td, fontFamily: "monospace" }}>{u.totp ? u.backup_left : "—"}</td>
                    <td style={td}>
                      <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        <input dir="ltr" value={val} placeholder="05XXXXXXXX" onChange={(e) => setPhones((p) => ({ ...p, [u.id]: e.target.value }))}
                          style={{ width: 130, padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "monospace", fontSize: 12.5 }} />
                        {dirty && <button type="button" disabled={busy} onClick={() => savePhone(u)} style={{ ...btn("#2563eb"), padding: "5px 10px", fontSize: 12 }}>حفظ</button>}
                      </span>
                    </td>
                    <td style={td}>{(u.totp || u.locked) && <button type="button" disabled={busy} onClick={() => resetUser(u)} style={{ ...btn("#fff", "#dc2626", "1px solid #fecaca"), padding: "5px 10px", fontSize: 12 }}>إعادة ضبط</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 8, lineHeight: 1.8 }}>«إعادة ضبط» لمن فقد جواله أو غيّره: يُلغى ربط تطبيقه فيربطه من جديد. الجوال يُستخدم لرمز واتساب فقط (بعد اعتماد القالب).</div>
      </div>
    </>
  );
}

export default function TwoFactorPage() {
  const [me, setMe] = useState(null);
  const load = useCallback(() => twoFactor.my().then((r) => setMe(r.data)).catch((e) => toast.error(errOf(e, "تعذّر تحميل الصفحة"))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div dir="rtl" style={{ padding: "20px 24px", maxWidth: 980 }}>
      <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 4px" }}>🔐 التحقق بخطوتين</h1>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>بعد الدخول بحساب Google يُطلب رمز من تطبيق المصادقة على جوالك — فلا يكفي الوصول إلى بريدك للدخول إلى النظام.</div>
      {!me ? <div style={{ ...card, color: "#94a3b8", fontSize: 13 }}>جارٍ التحميل…</div> : (
        <>
          <MySection me={me} reload={load} />
          {me.is_admin && <AdminSection me={me} reloadMe={load} />}
        </>
      )}
    </div>
  );
}
